import KeywordSpotlight from "@/components/KeywordSpotlight";
import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { services, serviceGroups, getService } from "@/data/services";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const Services = () => {
  useSeo({
    title: "Services · India Sourcing, QC, Audits · Asia Sourcing",
    description:
      "Independent India sourcing services: buying agent, QC inspection (AQL 2.5), factory audits, vendor + product development, OEM/private label, export logistics.",
    path: "/services",
    image: "/assets/og.jpg",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          name: "Asia Sourcing — Services",
          itemListElement: services.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/services/${s.id}`,
            name: s.name,
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
        ]),
      ],
    },
    jsonLdId: "services-hub-jsonld",
  });

  return (
    <article data-testid="services-hub-page">
      {/* HERO */}
      <header
        style={{
          position: "relative",
          minHeight: "52svh",
          background: "var(--ink)",
          color: "var(--paper)",
          display: "flex",
          alignItems: "flex-end",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(120% 80% at 20% 100%, rgba(227,200,138,.12) 0%, transparent 60%), linear-gradient(180deg, #1a1714 0%, #0f0d0b 100%)",
          }}
        />
        <div
          className="wrap"
          style={{
            position: "relative",
            zIndex: 2,
            paddingBlock: "calc(74px + var(--section)) clamp(2.5rem, 6vw, 5rem)",
            width: "100%",
          }}
        >
          <Reveal>
            <p className="eyebrow eyebrow-light" data-testid="services-eyebrow">
              Services · India sourcing, QC, audits, manufacturing
            </p>
          </Reveal>
          <WordReveal
            as="h1"
            text="The full sourcing chain — from brief to bill of lading."
            className="display"
            stagger={55}
            testId="services-headline"
          />
          <Reveal
            as="p"
            delay={180}
            style={{
              maxWidth: "62ch",
              color: "rgba(239,234,224,.78)",
              marginTop: "1.4rem",
              fontSize: "1.1rem",
              lineHeight: 1.65,
            }}
          >
            Twenty independent services across six disciplines — sourcing, inspection,
            audit, vendor & product development, manufacturing and export. Each one is
            transparent-fee, photo-documented and operated from Moradabad since 2002.
          </Reveal>
          <Reveal
            delay={300}
            style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", gap: ".8rem" }}
          >
            <Link
              to="/contact"
              className="btn btn-fill btn-lg btn-glow"
              data-testid="services-hero-enquiry"
            >
              <span>Start an enquiry →</span>
            </Link>
            <Link
              to="/services/buying-agent-india"
              className="btn btn-ghost"
              style={{
                borderColor: "rgba(239,234,224,.3)",
                color: "rgba(239,234,224,.85)",
              }}
              data-testid="services-hero-feature"
            >
              Start with: Buying Agent India
            </Link>
          </Reveal>
        </div>
      </header>

      {/* GROUPS */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)" }}
        data-testid="services-groups"
      >
        {serviceGroups.map((g, gi) => (
          <div
            key={g.id}
            style={{
              paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
              borderBottom:
                gi < serviceGroups.length - 1 ? "1px solid var(--line)" : "none",
            }}
            data-testid={`services-group-${g.id}`}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(220px, 320px) 1fr",
                gap: "clamp(1.5rem, 4vw, 4rem)",
                alignItems: "start",
              }}
              className="services-group-row"
            >
              <Reveal>
                <p
                  className="mono"
                  style={{
                    fontSize: ".72rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    color: "var(--brass-deep)",
                  }}
                >
                  / {String(gi + 1).padStart(2, "0")} · {g.id}
                </p>
                <h2
                  className="display"
                  style={{
                    fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)",
                    margin: "1rem 0 1rem",
                    letterSpacing: "-.01em",
                    lineHeight: 1.1,
                  }}
                >
                  {g.name}
                </h2>
                <p
                  style={{
                    color: "var(--ink-soft)",
                    fontSize: "1.02rem",
                    lineHeight: 1.65,
                    maxWidth: "40ch",
                  }}
                >
                  {g.tagline}
                </p>
              </Reveal>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                  gap: ".8rem",
                }}
              >
                {g.serviceIds.map((sid) => {
                  const s = getService(sid);
                  if (!s) return null;
                  return (
                    <Reveal key={sid}>
                      <Link
                        to={`/services/${s.id}`}
                        className="service-tile"
                        data-testid={`services-tile-${s.id}`}
                        style={{
                          display: "block",
                          padding: "1.1rem 1.2rem",
                          border: "1px solid var(--line)",
                          borderRadius: 2,
                          transition: "all .25s ease",
                          background: "var(--paper)",
                          height: "100%",
                        }}
                      >
                        <h3
                          style={{
                            fontFamily: "var(--display)",
                            fontWeight: 400,
                            fontSize: "1.08rem",
                            margin: "0 0 .35rem",
                            letterSpacing: "-.005em",
                            color: "var(--ink)",
                            lineHeight: 1.25,
                          }}
                        >
                          {s.name} →
                        </h3>
                        <p
                          style={{
                            color: "var(--ink-soft)",
                            fontSize: ".92rem",
                            margin: 0,
                            lineHeight: 1.55,
                          }}
                        >
                          {s.metaDescription}
                        </p>
                      </Link>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="cta wrap" style={{ borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">
          Start an enquiry
        </Reveal>
        <Reveal as="h2" className="display">
          One brief. One accountable team. One container.
        </Reveal>
        <Reveal className="row">
          <Link
            to="/contact"
            className="btn btn-fill btn-lg btn-glow"
            data-testid="services-cta-enquiry"
          >
            <span>Send an enquiry →</span>
          </Link>
          <Link to="/clusters" className="btn btn-ghost">
            See manufacturing clusters
          </Link>
        </Reveal>
      </section>
      <KeywordSpotlight type="services" />
    </article>
  );
};

export default Services;
